import React, { useState, useEffect, useRef } from 'react';
import axios from './utils/axiosConfig';
import { Table, Form, Button, Alert } from 'react-bootstrap';
import { hasPermission } from './utils/permissions';
import { useAuth } from './context/AuthContext';
import { isSuperAdmin } from './utils/superAdmin';

const TopicsTable = () => {
  const { user } = useAuth();
  
  // 狀態變數設定
  const [canEditRole, setCanEditRole] = useState(false);
  const [canDeleteTopic, setCanDeleteTopic] = useState(false);
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: 't_id', direction: 'desc' });
  const [successMessage, setSuccessMessage] = useState(""); // 成功訊息狀態
  const [errorMessage, setErrorMessage] = useState(""); // 錯誤訊息狀態
  const [tableKey, setTableKey] = useState(0); // 用於強制刷新表格
  const [lightboxImage, setLightboxImage] = useState(null);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768); // 檢測是否為手機端
  const [viewMode, setViewMode] = useState('auto'); // 顯示模式
  const observer = useRef();
  const lastTopicRef = useRef();

  // 檢查權限函數
  const checkPermission = async () => {
    const editRolePermission = await hasPermission(user, 'EDIT_ROLE');
    setCanEditRole(editRolePermission);
    
    // 檢查刪除權限：e_id=80 或各社秘書(e_position=17)
    const canDelete = user && (
      isSuperAdmin(user.e_id) || 
      parseInt(user.e_position) === 17
    );
    setCanDeleteTopic(canDelete);
  };
  

  const fetchTopics = async (page, sortKey = 't_id', sortDirection = 'asc', filter = '', isNewQuery = false) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/topics', {
        params: { filter, page, sortKey, sortDirection },
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (isNewQuery) {
        // 新查詢：重置資料
        setTopics(response.data);
      } else {
        // 分頁載入：累加資料
        setTopics((prevTopics) => [...prevTopics, ...response.data]);
      }
      setHasMore(response.data.length > 0);
    } catch (error) {
      // Error fetching topics
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchTopics(page, sortConfig.key, sortConfig.direction, filter, page === 1);
  }, [page, filter, sortConfig]);

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
    setPage(1);
    // 使用 isNewQuery=true 來重置資料
    fetchTopics(1, key, direction, filter, true);
  };

  const handleFilterChange = (e) => {
    setFilter(e.target.value);
    setPage(1);
    // 使用 isNewQuery=true 來重置資料
    fetchTopics(1, sortConfig.key, sortConfig.direction, e.target.value, true);
  };

  useEffect(() => {
    if (loading) return;
    if (observer.current) observer.current.disconnect();

    observer.current = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasMore) {
        setPage((prevPage) => prevPage + 1);
      }
    });

    if (lastTopicRef.current) {
      observer.current.observe(lastTopicRef.current);
    }
  }, [loading, hasMore]);

  const handleEdit = (topic) => {
    // 編輯邏輯，例如開啟編輯模態框
    setSuccessMessage("編輯完成！"); // 設置成功訊息
    setTimeout(() => setSuccessMessage(""), 3000); // 3 秒後清除訊息
  };

  const handleDelete = async (t_id) => {
    if (window.confirm('確定要刪除這條主題資料嗎？')) {
      try {
        const token = localStorage.getItem('token');
        await axios.delete(`/topics/${t_id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setTopics((prevTopics) => prevTopics.filter((topic) => topic.t_id !== t_id));
        setSuccessMessage("刪除成功！");
        setTimeout(() => setSuccessMessage(""), 3000);
  
        // 刪除後更改 key，強制表格重新渲染
        setTableKey((prevKey) => prevKey + 1);
      } catch (error) {
        let errorMsg = "刪除失敗";
        if (error.response?.data?.message) {
          errorMsg = error.response.data.message;
        } else if (error.message) {
          errorMsg = `刪除失敗: ${error.message}`;
        }
        setErrorMessage(errorMsg);
        setTimeout(() => setErrorMessage(""), 5000);
      }
    }
  };

  // 在 useEffect 中檢查權限
  useEffect(() => {
    checkPermission();
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // 監聽視窗大小變化
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className="container-fluid mt-4" key={tableKey}> {/* 設置 key 屬性 */}
    {/* 顯示成功訊息 */}
    {successMessage && (
      <Alert variant="success" onClose={() => setSuccessMessage("")} dismissible>
        {successMessage}
      </Alert>
    )}
    
    {/* 顯示錯誤訊息 */}
    {errorMessage && (
      <Alert variant="danger" onClose={() => setErrorMessage("")} dismissible>
        {errorMessage}
      </Alert>
    )}
      
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <Form.Control
          type="text"
          placeholder="搜尋主題資料..."
          value={filter}
          style={{ flex: '1', minWidth: '200px' }}
          onChange={handleFilterChange}
        />
        <div className="d-flex align-items-center gap-2">
          {/* 視圖切換按鈕 */}
          <div className="btn-group btn-group-sm" role="group">
            <Button 
              variant={viewMode === 'auto' ? 'primary' : 'outline-primary'}
              size="sm"
              onClick={() => setViewMode('auto')}
              title="自動切換"
            >
              📱
            </Button>
            <Button 
              variant={viewMode === 'table' ? 'primary' : 'outline-primary'}
              size="sm"
              onClick={() => setViewMode('table')}
              title="表格檢視"
            >
              📊
            </Button>
            <Button 
              variant={viewMode === 'card' ? 'primary' : 'outline-primary'}
              size="sm"
              onClick={() => setViewMode('card')}
              title="卡片檢視"
            >
              🃏
            </Button>
          </div>
        </div>
      </div>

      {/* 資料顯示區域 - 根據模式切換 */}
      {(viewMode === 'card' || (viewMode === 'auto' && isMobile)) ? (
        /* 卡片式佈局 - 適合手機端 */
        <div className="row">
          {topics.map((topic, index) => (
            <div key={topic.t_id} className="col-12 col-md-6 col-lg-4 mb-3" ref={topics.length === index + 1 ? lastTopicRef : null}>
              <div className="card h-100 shadow-sm">
                <div className="card-header d-flex justify-content-between align-items-center bg-dark text-white">
                  <h6 className="mb-0">{topic.t_name}</h6>
                  <span className="badge bg-primary">#{topic.t_id}</span>
                </div>
                <div className="card-body">
                  <div className="row g-2 small">
                    <div className="col-6">
                      <strong>社別:</strong><br/>
                      <span className="text-muted">{topic.store_name}</span>
                    </div>
                    <div className="col-6">
                      <strong>日期:</strong><br/>
                      <span className="text-muted">{topic.t_date}</span>
                    </div>
                    <div className="col-12">
                      <strong>講師:</strong><br/>
                      <span className="text-muted">{topic.lecturer_name}</span>
                    </div>
                    <div className="col-12">
                      <strong>建檔者:</strong><br/>
                      <span className="text-muted">{topic.employee_name}</span>
                    </div>
                    {topic.a_EDM && (
                      <div className="col-12">
                        <strong>海報:</strong><br/>
                        <div style={{ 
                          position: 'relative',
                          width: '100%',
                          cursor: 'pointer'
                        }}>
                          <img
                            src={`/uploads/AEDM/${topic.a_EDM}?t=${new Date().getTime()}`}
                            alt="活動海報"
                            style={{
                              width: '100%',
                              height: 'auto',
                              aspectRatio: '0.71',
                              objectFit: 'contain',
                              borderRadius: '6px',
                              border: '1px solid #007bff',
                              backgroundColor: '#fff'
                            }}
                            onClick={() => {
                              setLightboxImage(`/uploads/AEDM/${topic.a_EDM}?t=${new Date().getTime()}`);
                              setIsLightboxOpen(true);
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                {(canEditRole || canDeleteTopic) && (
                  <div className="card-footer bg-transparent">
                    <div className="d-flex gap-2">
                      {canEditRole && (
                        <Button 
                          variant="info" 
                          size="sm" 
                          className="flex-fill"
                          onClick={() => handleEdit(topic)}
                        >
                          編輯
                        </Button>
                      )}
                      {canDeleteTopic && (
                        <Button 
                          variant="danger" 
                          size="sm" 
                          className="flex-fill"
                          onClick={() => handleDelete(topic.t_id)}
                        >
                          刪除
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
          {loading && (
            <div className="col-12 text-center py-4">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">加載中...</span>
              </div>
            </div>
          )}
        </div>
      ) : (
        /* 表格式佈局 - 適合桌面端 */
        <div style={{ overflowX: 'auto' }}>
          <Table striped bordered hover variant="dark" className="table-striped table-dark" style={{ 
            tableLayout: 'auto',
            minWidth: '800px'
          }}>
          <thead>
            <tr>
              <th onClick={() => handleSort('t_id')} style={{ 
                width: '60px', 
                cursor: 'pointer',
                fontSize: '14px'
              }}>編號</th>
              <th onClick={() => handleSort('store_name')} style={{ 
                width: '80px', 
                cursor: 'pointer',
                fontSize: '14px'
              }}>社別</th>
              <th style={{ 
                width: '120px',
                fontSize: '14px'
              }}>海報</th>
              <th onClick={() => handleSort('t_name')} style={{ 
                minWidth: '200px', 
                cursor: 'pointer',
                fontSize: '14px'
              }}>主題</th>
              <th onClick={() => handleSort('lecturer_name')} style={{ 
                minWidth: '120px', 
                cursor: 'pointer',
                fontSize: '14px'
              }}>講師</th>
              <th onClick={() => handleSort('employee_name')} style={{ 
                minWidth: '100px', 
                cursor: 'pointer',
                fontSize: '14px'
              }}>建檔者</th>
              <th onClick={() => handleSort('t_date')} style={{ 
                width: '100px', 
                cursor: 'pointer',
                fontSize: '14px'
              }}>日期</th>
              {(canEditRole || canDeleteTopic) && (
                <th style={{ 
                  width: '80px',
                  fontSize: '14px'
                }}>操作</th>
              )}
            </tr>
          </thead>
        <tbody>
          {topics.map((topic, index) => (
            <tr key={topic.t_id} ref={topics.length === index + 1 ? lastTopicRef : null}>
              <td style={{ textAlign: 'center', fontSize: '14px' }}>{topic.t_id}</td>
              <td style={{ 
                wordBreak: 'break-word', 
                overflow: 'hidden',
                fontSize: '13px',
                padding: '8px'
              }}>
                {topic.store_name}
              </td>
              <td className="poster-container-topics" style={{ 
                textAlign: 'center', 
                padding: '4px',
                minWidth: '100px',
                maxWidth: '150px'
              }}>
                {topic.a_EDM ? (
                  <div style={{ 
                    position: 'relative',
                    width: '100%',
                    cursor: 'pointer'
                  }}>
                    <img
                      src={`/uploads/AEDM/${topic.a_EDM}?t=${new Date().getTime()}`}
                      alt="活動海報"
                      style={{
                        width: '100%',
                        height: 'auto',
                        aspectRatio: '0.71', // A4 比例 (210:297)
                        objectFit: 'contain',
                        borderRadius: '6px',
                        border: '1px solid #007bff',
                        transition: 'all 0.2s ease',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        backgroundColor: '#fff'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.transform = 'scale(1.02)';
                        e.target.style.boxShadow = '0 4px 16px rgba(0,0,0,0.2)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.transform = 'scale(1)';
                        e.target.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                      }}
                      onClick={() => {
                        setLightboxImage(`/uploads/AEDM/${topic.a_EDM}?t=${new Date().getTime()}`);
                        setIsLightboxOpen(true);
                      }}
                    />
                    <div style={{
                      position: 'absolute',
                      top: '4px',
                      right: '4px',
                      backgroundColor: 'rgba(0, 123, 255, 0.9)',
                      color: 'white',
                      borderRadius: '12px',
                      width: '20px',
                      height: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '10px',
                      cursor: 'pointer',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                      backdropFilter: 'blur(4px)'
                    }}>
                      🔍
                    </div>
                  </div>
                ) : (
                  <div style={{
                    width: '100%',
                    aspectRatio: '0.71',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '6px',
                    border: '1px dashed #dee2e6',
                    color: '#6c757d',
                    fontSize: '11px',
                    minHeight: '60px'
                  }}>
                    無海報
                  </div>
                )}
              </td>
              <td style={{ 
                wordBreak: 'break-word', 
                fontSize: '14px',
                padding: '8px'
              }}>
                {topic.t_name}
              </td>
              <td style={{ 
                wordBreak: 'break-word', 
                fontSize: '14px',
                padding: '8px'
              }}>
                {topic.lecturer_name}
              </td>
              <td style={{ 
                wordBreak: 'break-word', 
                overflow: 'hidden',
                fontSize: '13px',
                padding: '8px'
              }}>
                {topic.employee_name}
              </td>
              <td style={{ 
                textAlign: 'center', 
                fontSize: '13px',
                padding: '8px'
              }}>
                {topic.t_date}
              </td>
              {/* <td>{topic.t_edit_day}</td>
              <td>{topic.t_remark}</td> */}
              {(canEditRole || canDeleteTopic) && (
                <td style={{ 
                  textAlign: 'center', 
                  padding: '8px',
                  whiteSpace: 'nowrap'
                }}>
                  {canEditRole && (
                    <Button 
                      variant="info" 
                      size="sm" 
                      style={{ marginRight: '4px', fontSize: '12px' }}
                      onClick={() => handleEdit(topic)}
                    >
                      編輯
                    </Button>
                  )}
                  {canDeleteTopic && (
                    <Button 
                      variant="danger" 
                      size="sm" 
                      style={{ fontSize: '12px' }}
                      onClick={() => handleDelete(topic.t_id)}
                    >
                      刪除
                    </Button>
                  )}
                </td>
              )}
            </tr>
          ))}
          {loading && (
            <tr>
              <td colSpan={(canEditRole || canDeleteTopic) ? 8 : 7} className="text-center">
                加載中...
              </td>
            </tr>
          )}
        </tbody>
      </Table>
      </div>
      )}

      {isLightboxOpen && (
        <div 
          style={{
            position: 'fixed', 
            top: 0, 
            left: 0, 
            width: '100%', 
            height: '100%', 
            backgroundColor: 'rgba(0, 0, 0, 0.9)', 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            zIndex: 1050,
            backdropFilter: 'blur(5px)'
          }}
          onClick={() => setIsLightboxOpen(false)}
        >
          <div style={{ position: 'relative' }}>
            <img 
              src={lightboxImage} 
              alt="海報 Lightbox"
              style={{ 
                maxWidth: '90vw', 
                maxHeight: '90vh', 
                objectFit: 'contain',
                borderRadius: '12px',
                boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
                border: '3px solid white'
              }}
            />
            <button
              style={{
                position: 'absolute',
                top: '-15px',
                right: '-15px',
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                border: 'none',
                backgroundColor: '#ff4757',
                color: 'white',
                fontSize: '20px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                transition: 'transform 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.transform = 'scale(1.1)'}
              onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
              onClick={(e) => {
                e.stopPropagation();
                setIsLightboxOpen(false);
              }}
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TopicsTable;
